/**
 * Current-user hooks. The signed-in user is fetched from
 * `GET /api/shared/me` and cached under `queryKeys.me`; the Settings
 * identity line, the home-layout switch, and the Today page all read
 * from the same cache entry.
 */

import type { CurrentUserDTO, DocumentAiModel, HomeLayout } from '@almanac/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';
import { queryKeys } from './queryClient.js';

export function useCurrentUser() {
  return useQuery<CurrentUserDTO>({
    queryKey: queryKeys.me,
    queryFn: api.me,
    // Identity + preferences rarely change within a session; a reload
    // (which re-runs Access) is the refresh.
    staleTime: 1000 * 60 * 60,
  });
}

/**
 * Set the per-user home layout. Optimistically patches the `me` cache
 * so the Today screen flips immediately, then reconciles with the
 * server's returned user (rolling back on error).
 */
export function useSetHomeLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (layout: HomeLayout) => api.updateMe({ homeLayout: layout }),
    onMutate: async (layout) => {
      await qc.cancelQueries({ queryKey: queryKeys.me });
      const previous = qc.getQueryData<CurrentUserDTO>(queryKeys.me);
      if (previous) {
        qc.setQueryData<CurrentUserDTO>(queryKeys.me, { ...previous, homeLayout: layout });
      }
      return { previous };
    },
    onError: (_err, _layout, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.me, ctx.previous);
    },
    onSuccess: (user) => {
      qc.setQueryData(queryKeys.me, user);
    },
  });
}

/**
 * Set the per-user AI model for document extraction. Optimistically
 * patches the `me` cache so the Settings selector updates immediately,
 * then reconciles with the server's returned user (rolling back on
 * error).
 */
export function useSetDocumentAiModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (model: DocumentAiModel) => api.updateMe({ documentAiModel: model }),
    onMutate: async (model) => {
      await qc.cancelQueries({ queryKey: queryKeys.me });
      const previous = qc.getQueryData<CurrentUserDTO>(queryKeys.me);
      if (previous) {
        qc.setQueryData<CurrentUserDTO>(queryKeys.me, { ...previous, documentAiModel: model });
      }
      return { previous };
    },
    onError: (_err, _model, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.me, ctx.previous);
    },
    onSuccess: (user) => {
      qc.setQueryData(queryKeys.me, user);
    },
  });
}

/**
 * Set the display currency for the Budget page. Null returns to the
 * CHF default. Same optimistic pattern as the other preference setters.
 */
export function useSetCurrency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (currency: string | null) => api.updateMe({ currency }),
    onMutate: async (currency) => {
      await qc.cancelQueries({ queryKey: queryKeys.me });
      const previous = qc.getQueryData<CurrentUserDTO>(queryKeys.me);
      if (previous) {
        qc.setQueryData<CurrentUserDTO>(queryKeys.me, { ...previous, currency });
      }
      return { previous };
    },
    onError: (_err, _currency, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.me, ctx.previous);
    },
    onSuccess: (user) => {
      qc.setQueryData(queryKeys.me, user);
    },
  });
}

/**
 * Link (or clear) the "This is me" person record (Settings → Account).
 * Same optimistic pattern as the other preference setters.
 */
export function useSetPersonLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (personId: string | null) => api.updateMe({ personId }),
    onMutate: async (personId) => {
      await qc.cancelQueries({ queryKey: queryKeys.me });
      const previous = qc.getQueryData<CurrentUserDTO>(queryKeys.me);
      if (previous) {
        qc.setQueryData<CurrentUserDTO>(queryKeys.me, { ...previous, personId });
      }
      return { previous };
    },
    onError: (_err, _personId, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.me, ctx.previous);
    },
    onSuccess: (user) => {
      qc.setQueryData(queryKeys.me, user);
    },
  });
}
